import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

import {
  cambiarActivo,
  corregirDocumento,
  corregirVarios,
  crearChofer,
  traerDocumentacion,
  traerPins,
} from "../services/documentacion";
import {
  DIAS_AVISO,
  DIAS_PRORROGA,
  coincide,
  diasHasta,
  estaDeBaja,
  evaluar,
  ordenar,
  ordenarPor,
  soloActivos,
  soloDeBaja,
} from "../shared/documentacion";
import { desconocidas } from "../shared/zona";
import {
  desconocidas as siglasRaras,
  nombreDe as empresaDe,
} from "../shared/empresa";
import {
  LARGA,
  MOV,
  SIN_DATO as TIPO_SIN_DATO,
  contar as contarTipos,
  etiquetaTipo,
  tipoDe,
} from "../shared/tipoServicio";
import { aWhatsapp } from "../shared/telefono";
import {
  alternar,
  desmarcarVisibles,
  marcadas,
  marcarVisibles,
  resumen as resumenSeleccion,
  todasVisiblesMarcadas,
} from "../shared/seleccion";
import ModoEdicion from "../components/funcionales/ModoEdicion";
import BarraSeleccion from "../components/funcionales/BarraSeleccion";
import AltaChofer from "../components/funcionales/AltaChofer";
import SoltarRegistro from "../components/funcionales/SoltarRegistro";
import ChipUsuario from "../components/funcionales/ChipUsuario";
import TraerPlanilla from "../components/funcionales/TraerPlanilla";
import Loading from "./Loading";

/* La documentación de los choferes.
 *
 * QUÉ RESUELVE. Los vencimientos vivían en una planilla del Drive y sólo se
 * podían corregir abriendo el Excel o mandándole un mensaje al bot. No había
 * ninguna pantalla donde escribir. Ésta es esa pantalla, y muestra lo mismo que
 * la planilla —las mismas columnas, en el mismo orden— para que nadie tenga que
 * reaprender dónde mira.
 *
 * UNA FILA POR PERSONA, NO POR DOCUMENTO. La unidad de trabajo no es el papel:
 * es **la persona a la que hay que ir a buscar**, y a esa persona se la llama
 * una vez aunque le falten dos.
 *
 * EL CRITERIO NO VIVE ACÁ. Está en `shared/documentacion.js`, apareado con
 * `ficha.py` de FlotaBot. El bot avisa cuando un camión vuelve a base y esta
 * pantalla resuelve ese aviso: si los criterios se separan, el mail dice que
 * falta el registro y la pantalla lo muestra en verde.
 *
 * LO QUE SE MUESTRA PERO NO CUENTA: el psicofísico. Hace falta para RENOVAR el
 * registro, no para salir a la ruta, y no entra en el semáforo — meterlo
 * convertiría la lista en una que nadie mira. **Y por eso mismo dejó de tener
 * columna propia**: vive adentro de la del registro, que es lo que sirve para
 * renovar (ver `PsicofisicoDelRegistro`).
 *
 * EL CURSO NO ESTÁ. Ya no se usa. Una columna muerta en una pantalla nueva es
 * una que dentro de un año nadie sabe si hay que mirar.
 *
 * EL PIN DE LA TARJETA ES OTRA COSA. Ver `PinYPF` acá abajo.
 */

/* «De baja» va al final y **sólo aparece si hay alguien** — un «De baja · 0»
 * es un cartel que hay que leer para descartar, igual que el conteo de la
 * prórroga. Y existe como solapa y no sólo como resultado de buscar: sin ella,
 * reactivar a alguien obligaría a acordarse del nombre de quien ya no está en
 * ninguna lista. */
const VISTAS = [
  ["resolver", "Hay que resolver"],
  ["prorroga", "Con prórroga"],
  ["mes", "Vencen este mes"],
  ["todos", "Todos"],
  ["baja", "De baja"],
];

const fmt = (f) => {
  if (!f) return null;
  const [a, m, d] = String(f).slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
};

/* La celda de un vencimiento. `null` NO se dibuja como "al día": se dice que
 * falta la fecha, que es el hueco más accionable que hay.
 *
 * ── Y LA FECHA VA EN TODOS LOS CASOS, NO SÓLO EN «AL DÍA» ────────────────
 *
 * Antes sólo la imprimía ahí, y la de los vencidos la ponía `FechaEditable`
 * como texto. Resultado: **en las filas al día la fecha se veía DOS VECES**
 * —«al día 21/07/2027 21/07/2027»— y en las vencidas una. Dos componentes
 * dibujando el mismo dato sin saber uno del otro.
 *
 * Ahora esta celda es la única que dice CUÁNDO vence, en los cuatro casos, y
 * `FechaEditable` se dibuja sólo cuando se está editando. */
const Celda = ({ fecha, haceFalta = true, prorroga = null }) => {
  if (!haceFalta) return <span className="tenue">no le hace falta</span>;
  const dias = diasHasta(fecha);
  /* Sin fecha no hay nada que poner al lado: el hueco ES el dato. */
  if (dias === null) return <span className="pill mal">sin fecha</span>;

  /* CON PRÓRROGA SE MUESTRAN LAS DOS COSAS, y en ese orden: el HECHO —venció
   * hace 41 días— y arriba de él el estado, que es lo que decide si sale.
   * Sólo la prórroga se leería como que el registro está bien; sólo el
   * vencimiento, como que no puede salir. Las dos juntas es lo que pasa de
   * verdad, y la fecha límite va al lado porque «quedan 19» obliga a hacer la
   * cuenta para sacar un turno. */
  if (prorroga) {
    return (
      <span className="doc-ok">
        <span className="pill prorroga">
          prórroga · {prorroga.quedan} d
        </span>
        <span className="sub">
          venció hace {prorroga.vencidoHace} d · puede salir hasta el{" "}
          {fmt(prorroga.hasta)}
        </span>
      </span>
    );
  }

  const tono = dias < 0 ? "mal" : dias <= DIAS_AVISO ? "ojo" : "ok";
  const texto =
    dias < 0
      ? `venció hace ${Math.abs(dias)} d`
      : dias <= DIAS_AVISO
        ? `en ${dias} días`
        : "al día";

  return (
    <span className="doc-ok">
      <span className={"pill " + tono}>{texto}</span>
      <span className="sub">{fmt(fecha)}</span>
    </span>
  );
};

/* EL PSICOFÍSICO NO TIENE COLUMNA: VIVE ADENTRO DEL REGISTRO ────────────────
 *
 * Y no es sólo para ahorrar una columna en una tabla de diez. Es **para qué
 * sirve**: el psicofísico hace falta para RENOVAR el registro, no para salir a
 * la ruta. O sea que la única vez que hay que mirarlo es cuando el registro se
 * está por vencer, ya venció, o está en prórroga — que es justo cuando alguien
 * va a sacar el turno. Como columna propia estaba a la vista de los 224 todo el
 * tiempo, y con 142 vencidos de 153 eso es una columna en rojo permanente que
 * se deja de mirar en una semana.
 *
 * Puesto acá abajo dice lo que es: **un requisito del renglón de arriba.**
 *
 * ── PERO NO PUEDE LEERSE COMO QUE FRENA LA SALIDA ────────────────────────
 *
 * Es la mitad delicada. Un vencimiento debajo de otro vencimiento se lee como
 * dos cosas que frenan al camión, y ésta no frena nada — es el mismo criterio
 * que `docs_que_avisan()` en FlotaBot y el que `evaluar` ya aplica al no
 * contarlo en `faltan`. Por eso va en gris, con la palabra «psicofísico»
 * adelante y con **para qué hace falta escrito en la misma línea**.
 *
 * ── Y CON EL LÁPIZ APARECE EN TODAS LAS FILAS ────────────────────────────
 *
 * Si sólo se dibujara donde el registro está por vencer, el psicofísico de un
 * chofer con el registro al día **no se podría corregir desde ninguna
 * pantalla**: esta columna era el único lugar donde se editaba. Se muestra
 * donde importa y se edita siempre — que es lo que permite sacar la columna sin
 * perder la función.
 */
const PsicofisicoDelRegistro = ({ fila, tocando, onGuardar }) => {
  const dias = diasHasta(fila.psicofisico_vence);
  const registro = diasHasta(fila.registro_vence);

  /* Con el registro al día y sin el lápiz no se dibuja nada: no hay nada que
   * hacer con ese dato hoy, y un renglón más en 200 filas es ruido. */
  const importa = registro === null || registro <= DIAS_AVISO;
  if (!importa && !tocando) return null;

  const texto =
    dias === null
      ? "sin fecha de psicofísico"
      : dias < 0
        ? `psicofísico venció hace ${Math.abs(dias)} d`
        : `psicofísico al día · ${fmt(fila.psicofisico_vence)}`;

  return (
    <span className="doc-psico">
      <span className="sub">
        {texto}
        {/* PARA QUÉ HACE FALTA, EN LA MISMA LÍNEA. Sin esto, un «venció hace
            120 d» debajo del registro se lee como un segundo papel que frena
            al camión — y frenar a alguien que sí puede salir termina en una
            llamada al portón para destrabar algo que estaba bien. */}
        {dias !== null && dias >= 0
          ? null
          : <> · <i>hace falta para renovar el registro, no frena la salida</i></>}
      </span>
      {tocando && (
        <FechaEditable
          valor={fila.psicofisico_vence}
          puedeEditar
          soloEdicion
          onGuardar={onGuardar}
        />
      )}
    </span>
  );
};

/* Un encabezado que ordena. El ciclo es ascendente → descendente → el orden de
 * urgencia de siempre, y la flecha dice en cuál está. Sin el tercer paso, un
 * click te deja sin la lista de trabajo hasta recargar la pantalla. */
const ThOrden = ({ columna, orden, alTocar, children, titulo }) => {
  const activo = orden.columna === columna;
  const flecha = !activo ? "" : orden.desc ? " ↓" : " ↑";
  return (
    <th>
      <button
        type="button"
        className={"th-orden" + (activo ? " activo" : "")}
        onClick={() => alTocar(columna)}
        title={titulo}
      >
        {children}
        <span className="th-flecha">{flecha}</span>
      </button>
    </th>
  );
};

/* El teléfono, con el atajo para escribirle.
 *
 * SE MUESTRA SIEMPRE Y SE ENLAZA CUANDO SE PUEDE. El número es el dato; el
 * enlace es la comodidad. Si `aWhatsapp` no lo pudo entender —una nota en vez
 * de un número, dígitos de menos— se muestra el texto tal cual y sin enlace:
 * un `wa.me` mal armado no falla, abre el chat de otra persona y el mensaje se
 * manda igual. Ver `shared/telefono.js`.
 *
 * El enlace abre en otra pestaña porque el hub vive adentro de un iframe: sin
 * `_blank` WhatsApp Web intenta cargarse ahí adentro y se ve como que la
 * pantalla se rompió.
 */
const Telefono = ({ tel, nombre }) => {
  /* El botón de copiar (#104, Marcos): el enlace abre el chat, pero para
   * pegar el número en otro lado —un mail, una planilla— había que tipearlo
   * mirando la pantalla. Copia los dígitos pelados, que es lo que cualquier
   * destino acepta, y avisa con el ✓ en el propio botón: un toast por copiar
   * un teléfono es más ruido que función. */
  const [copiado, setCopiado] = useState(false);
  if (!tel) return <span className="tenue">—</span>;
  const copiar = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(tel).replace(/\D/g, "") || String(tel));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      /* Sin clipboard (iframe viejo) el botón no promete lo que no puede. */
    }
  };
  const botonCopiar = (
    <button
      type="button"
      className="doc-copiar"
      onClick={copiar}
      title={copiado ? "Copiado" : "Copiar el número"}
      aria-label={`Copiar el teléfono de ${nombre || "este chofer"}`}
    >
      {copiado ? "✓" : "⧉"}
    </button>
  );
  const url = aWhatsapp(tel);
  if (!url)
    return (
      <span className="dato">
        {tel} {botonCopiar}
      </span>
    );
  return (
    <>
      <a
        className="dato doc-wa"
        href={url}
        target="_blank"
        rel="noreferrer"
        title={`Escribirle por WhatsApp a ${nombre || "este chofer"}`}
      >
        {tel}
      </a>{" "}
      {botonCopiar}
    </>
  );
};

/* El teléfono, escribible. Al salir del campo vuelve a ser el enlace.
 *
 * ── LO QUE HACE QUE ESTO NO SEA UN INPUT MÁS ─────────────────────────────
 *
 * **Dice MIENTRAS SE ESCRIBE si va a quedar enlazado.** `aWhatsapp` no arma el
 * enlace con cualquier cosa: Argentina completa siempre diez dígitos entre
 * área y abonado, y lo que no llega a diez se muestra como texto pelado. Sin
 * el aviso en el momento, el que carga un número corto lo guarda, ve que la
 * celda no se volvió celeste y no tiene forma de saber por qué — el campo
 * habría aceptado el dato y la función no estaría.
 *
 * Y avisa **sin frenar**: se guarda igual. Un número a medias es un dato que
 * alguien anotó y que se corrige; rechazarlo cambia «hay algo mal cargado»
 * —visible— por «no se pudo cargar». Misma regla que `duplicados`.
 *
 * ── SE GUARDA COMO SE ESCRIBIÓ ───────────────────────────────────────────
 *
 * No se normaliza al guardar. La planilla trae `011 15 6789 0123`,
 * `2964 60 7411` y `02334 405335`, y todas son legibles para una persona;
 * reescribirlas en una forma canónica haría que el número deje de parecerse al
 * que está anotado en el papel del que se copió. La normalización es para
 * COMPARAR y para armar el enlace, no para almacenar — que es exactamente lo
 * que ya hace `shared/telefono.js`.
 */
/* El armazón compartido: un input que guarda AL SALIR, con su acuse.
 *
 * Vivía adentro de `TelefonoEditable` y ahora lo usa también la dirección.
 * Copiado serían dos definiciones de qué pasa cuando el guardado falla, y el
 * día que difieran una de las dos dejaría el valor nuevo en pantalla sobre una
 * fila que no se guardó — que es la mentira más cara que puede decir un campo.
 *
 * SE VUELVE AL QUE HABÍA SI FALLA. Dejar el nuevo haría creer que entró. */
const CampoEditable = ({
  valor, onGuardar, etiqueta, tipo = "text", placeholder, children,
}) => {
  const [v, setV] = useState(valor || "");
  const [estado, setEstado] = useState("");

  useEffect(() => setV(valor || ""), [valor]);

  return (
    <span className={"doc-fecha" + (estado ? " " + estado : "")}>
      <input
        type={tipo}
        value={v}
        placeholder={placeholder}
        onChange={(e) => setV(e.target.value)}
        onBlur={async () => {
          const nuevo = String(v || "").trim() || null;
          if (nuevo === (valor || null)) return;
          setEstado("yendo");
          try {
            await onGuardar(nuevo);
            setEstado("ok");
            setTimeout(() => setEstado(""), 1500);
          } catch {
            setV(valor || "");
            setEstado("mal");
          }
        }}
        aria-label={etiqueta}
      />
      {typeof children === "function" ? children(v, estado) : children}
      {estado === "mal" && <i title="No se pudo guardar">⚠</i>}
      {estado === "ok" && <i title="Guardado">✓</i>}
    </span>
  );
};

const TelefonoEditable = ({ valor, onGuardar, puedeEditar }) => {
  // Fuera del modo edición no se dibuja nada: el enlace ya lo muestra
  // `Telefono` arriba, y el dato saldría dos veces.
  if (!puedeEditar) return null;
  return (
    <CampoEditable
      valor={valor}
      onGuardar={onGuardar}
      etiqueta="Teléfono"
      tipo="tel"
      placeholder="11 5555 5555"
    >
      {(v, estado) =>
        /* El aviso va mientras se escribe, no después de guardar. */
        String(v || "").trim() && !aWhatsapp(v) && estado !== "mal" ? (
          <i
            className="sin-dato"
            title="Con este número no se puede armar el enlace: Argentina completa diez dígitos entre área y abonado. Se guarda igual."
          >
            sin enlace
          </i>
        ) : null
      }
    </CampoEditable>
  );
};

/* ── LA DIRECCIÓN NO ES LA ZONA, Y VAN EN COLUMNAS DISTINTAS ─────────────
 *
 * Lo pidió Marcos. `zona` es el PARTIDO y decide la prórroga de 60 días del
 * registro; `direccion` es la CALLE y no decide nada. Escribir una en la otra
 * haría que `clasificar()` no reconozca el partido y ese chofer pierda la
 * prórroga **sin fallar en ningún lado**: la pantalla diría «no puede salir» y
 * el síntoma no señalaría a la dirección.
 *
 * Por eso son dos columnas rotuladas y no una celda con las dos cosas: la
 * separación tiene que verse, no deducirse. Es la misma decisión que ya está
 * escrita en `registroLeido.js` para la lectura del registro.
 */
const DireccionEditable = ({ valor, onGuardar, puedeEditar }) => {
  if (!puedeEditar) return <span className="tenue">{valor || "—"}</span>;
  return (
    <CampoEditable
      valor={valor}
      onGuardar={onGuardar}
      etiqueta="Dirección"
      placeholder="Av. Mitre 1234"
    />
  );
};

/* Dar de baja al chofer, y volver a darle de alta.
 *
 * ── EL BOTÓN DICE LA ACCIÓN, NO EL ESTADO ────────────────────────────────
 *
 * Ésta es la trampa que ya nos comimos con el booleano de portería: un botón
 * solo que muestra el estado actual no se puede leer — «DE BAJA» puede ser lo
 * que ES o lo que va a PASAR si lo tocás, y no hay forma de saber cuál. Acá el
 * estado se ve al lado del nombre («de baja», en gris) y el botón dice lo que
 * hace: «Dar de baja» o «Reactivar».
 *
 * ── NO PREGUNTA, Y NO ES UN DESCUIDO ─────────────────────────────────────
 *
 * Un cartel de confirmación es para lo que no se puede deshacer. Esto escribe
 * una columna, no borra nada, y el botón de al lado lo revierte con un clic.
 * Pedir confirmación de algo reversible enseña a apretar «sí» sin leer, y
 * después el mismo reflejo se aplica al borrado que sí se lleva algo puesto.
 */
const BotonBaja = ({ deBaja, onCambiar }) => {
  const [estado, setEstado] = useState("");

  return (
    <span className={"doc-baja" + (estado ? " " + estado : "")}>
      <button
        type="button"
        className="link-button"
        disabled={estado === "yendo"}
        onClick={async () => {
          setEstado("yendo");
          try {
            await onCambiar(deBaja);
            setEstado("");
          } catch {
            // «No se pudo» no se dice como «listo»: sin esto, la fila se queda
            // igual y parece que el botón no hace nada.
            setEstado("mal");
          }
        }}
      >
        {estado === "yendo"
          ? "Guardando…"
          : deBaja
            ? "Reactivar"
            : "Dar de baja"}
      </button>
      {estado === "mal" && <i title="No se pudo guardar">⚠</i>}
    </span>
  );
};

/* EL PIN DE LA TARJETA YPF.
 *
 * VIENE EN EL LISTADO, y esto se cambió a pedido. Antes se pedía de a uno con
 * un ojito, con el argumento de que traer 210 credenciales para usar una es
 * repartirlas. El argumento vale en general y acá no: **el PIN sin la tarjeta
 * física no sirve para nada**, y el que lo necesita lo necesita al lado del
 * teléfono de la persona, no a un clic por fila.
 *
 * LO QUE NO CAMBIÓ ES LO QUE RESTRINGE: la RLS de `chofer_pin` exige
 * `ver.pin_ypf` del lado de la BASE. Sin el permiso la consulta vuelve vacía
 * aunque se la llame desde la consola. La pantalla no decide nada.
 *
 * TRES RESPUESTAS Y NO DOS: el PIN, «no tiene uno cargado», y «no lo podés
 * ver». Confundir las dos últimas manda a alguien a cargar un PIN que existe.
 */
const PinYPF = ({ pin, puedeVer, hayTabla }) => {
  if (!puedeVer) return <span className="tenue">—</span>;
  if (pin) return <b className="dato">{pin}</b>;
  /* «Todavía no se importó ninguno» y «a esta persona le falta» se ven igual
   * si los dos dicen lo mismo — y mandan a hacer cosas opuestas: una es correr
   * la importación, la otra es ir a buscar el número de una tarjeta. */
  return (
    <span className="sub sin-dato">
      {hayTabla ? "sin PIN" : "sin importar"}
    </span>
  );
};

/* Una fecha que se puede corregir sin abrir un formulario. Se guarda al salir
 * del campo y no con un botón: con 210 filas, un "Guardar" por fila es una
 * pantalla que nadie usa. */
const FechaEditable = ({ valor, onGuardar, puedeEditar, soloEdicion = false }) => {
  const [v, setV] = useState(valor || "");
  const [estado, setEstado] = useState("");

  useEffect(() => setV(valor || ""), [valor]);

  /* `soloEdicion` es para las columnas donde `Celda` YA muestra la fecha:
   * fuera del modo edición no se dibuja nada, o el dato saldría dos veces.
   *
   * Donde nadie más la muestra —el psicofísico, que va aparte porque se ve y
   * no avisa— sigue saliendo como TEXTO y no como un campo apagado: un input
   * deshabilitado parece un lugar donde escribir, y el que lo intenta no
   * entiende por qué no pasa nada. */
  if (!puedeEditar) {
    return soloEdicion ? null : (
      <span className="tenue">{fmt(valor) || "—"}</span>
    );
  }

  return (
    <span className={"doc-fecha" + (estado ? " " + estado : "")}>
      <input
        type="date"
        value={v ? String(v).slice(0, 10) : ""}
        onChange={(e) => setV(e.target.value)}
        onBlur={async () => {
          const nuevo = v || null;
          if (nuevo === (valor || null)) return;
          setEstado("yendo");
          try {
            await onGuardar(nuevo);
            setEstado("ok");
            setTimeout(() => setEstado(""), 1500);
          } catch {
            // Se vuelve al valor que había: dejar el nuevo en pantalla haría
            // creer que se guardó.
            setV(valor || "");
            setEstado("mal");
          }
        }}
      />
      {estado === "mal" && <i title="No se pudo guardar">⚠</i>}
      {estado === "ok" && <i title="Guardado">✓</i>}
    </span>
  );
};

const Documentacion = () => {
  /* `permisos` llega por el contexto del Outlet y no como prop: el armazón ya
   * los tiene pedidos para dibujar el menú, y es el MISMO que ve el rail — así
   * que esta pantalla respeta «Ver como» sin hacer nada. */
  const { permisos = null } = useOutletContext() || {};
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState(null);
  const [vista, setVista] = useState("resolver");
  const [texto, setTexto] = useState("");
  /* P-113 (Aldo): el corte por vencimiento del registro. Vacío = sin corte. */
  const [hasta, setHasta] = useState("");
  /* `columna: null` es el orden de urgencia, que es el default y al que se
   * puede volver. Vive en la pantalla y no en `preferencias`: ordenar por
   * nombre es algo que se hace para buscar una fila y después se suelta, no
   * una forma de trabajar que haya que recordar entre sesiones. */
  const [orden, setOrden] = useState({ columna: null, desc: false });
  const [pins, setPins] = useState(null);
  /* ARRANCA APAGADO SIEMPRE, y no se recuerda entre visitas. Recordarlo haría
   * que alguien vuelva a la pantalla al día siguiente sin acordarse de que la
   * dejó abierta, y ahí un clic sobre una fecha vuelve a ser una edición. */
  const [editando, setEditando] = useState(false);
  /* La selección se guarda por DNI y NO como filas: así sobrevive a que se
   * cambie el filtro, se ordene la tabla o se vuelva a pedir la lista. Es lo
   * que permite marcar seis buscando «RGD» y cuatro más buscando otra cosa. */
  const [seleccion, setSeleccion] = useState(() => new Set());
  /* LAS FILAS RECIÉN CORREGIDAS, que se quedan a la vista aunque ya no les
   * toque la solapa. En «Hay que resolver», poner la fecha que faltaba saca a
   * la fila de la solapa en el mismo instante en que se guarda —antes de que
   * aparezca el ✓—, y lo que se ve es una fila que se borró. Se guarda bien;
   * parece lo contrario.
   *
   * Por DNI, igual que la selección, y con el veredicto que tenía al
   * corregirla: es lo que la deja EN SU LUGAR en vez de mandarla al fondo con
   * los que están al día. Se sueltan cuando la persona cambia de solapa,
   * busca o aprieta «Terminar». Los números de arriba no esperan: cuentan lo
   * que falta de verdad. */
  const [quietas, setQuietas] = useState(() => new Map());
  const soltarQuietas = () => setQuietas((m) => (m.size ? new Map() : m));
  const [guardandoBulk, setGuardandoBulk] = useState(false);
  const [resultadoBulk, setResultadoBulk] = useState(null);
  const [dandoDeAlta, setDandoDeAlta] = useState(false);
  const [soltando, setSoltando] = useState(false);

  /* `permisos` es un ARRAY, no un Set — así lo arma `misPermisos` y así lo lee
   * `Novedades` con `.includes`. Acá decía `.has(...)`, que no existe en un
   * array: tiraba un TypeError en el render y, sin nada que lo atrapara,
   * dejaba el HUB ENTERO en blanco. Ahora además hay un límite de error
   * (`SiSeRompe`), pero el uso correcto es éste. */
  const puedeVerPin = Boolean(permisos?.includes?.("ver.pin_ypf"));
  /* Mientras los permisos no llegaron se asume que puede. Esconder el lápiz
   * medio segundo y después mostrarlo se lee como que la pantalla titila
   * — misma regla que `puedeEditar` del rail. */
  const puedeEditar = !permisos || permisos.includes("cargar.documentos");
  /* Para los botones de sistema (P-110). Acá NO se asume mientras carga: el
   * usuario común no es admin y el parpadeo le mostraría botones ajenos. */
  const esAdmin = Boolean(permisos?.includes?.("admin.permisos"));
  /* PODER Y ESTAR EDITANDO SON DOS COSAS. La primera es el permiso; la segunda,
   * haber dicho que sí. Antes eran una sola y por eso la pantalla se abría con
   * los 210 campos vivos: un clic de más sobre una fecha ya era una corrección
   * guardada y firmada con tu nombre. */
  const tocando = puedeEditar && editando;

  const traer = useCallback(async () => {
    try {
      setFilas(await traerDocumentacion());
    } catch (e) {
      setError(e.message || "No se pudo leer la documentación");
    }
    /* LOS PIN VAN EN SU PROPIO `try`, y por el mismo motivo que el peso y el
     * nivel de urea: un problema leyendo la tabla de credenciales no puede
     * dejar sin documentación a la pantalla entera. Sin permiso la RLS
     * devuelve vacío —no error— así que esto no rebota, contesta `{}`. */
    try {
      setPins(await traerPins());
    } catch {
      setPins(null);
    }
  }, []);

  useEffect(() => {
    traer();
  }, [traer]);

  const conVeredicto = useMemo(
    () => ordenar((filas || []).map((f) => ({ ...f, _r: evaluar(f) }))),
    [filas],
  );

  /* ── EL QUE YA NO MANEJA NO CUENTA ────────────────────────────────────
   *
   * `conVeredicto` son TODOS —la búsqueda tiene que poder encontrarlos— y
   * `activos` es el universo de cada número y de cada solapa. Si no maneja, su
   * registro vencido no frena ninguna salida: contarlo infla justo el número
   * que decide si alguien abre esta pantalla.
   *
   * Y no desaparecen: tienen su propia solapa, la búsqueda los sigue trayendo,
   * y cuántos son se dice arriba de la tabla. Esconder en silencio haría que
   * «faltan 12» y «faltan 12 y hay 3 que ni miro» se lean igual. */
  const activos = useMemo(() => soloActivos(conVeredicto), [conVeredicto]);
  const deBaja = useMemo(() => soloDeBaja(conVeredicto), [conVeredicto]);

  const resolver = activos.filter((f) => f._r.estado === "faltan");
  const conProrroga = activos.filter((f) => f._r.estado === "prorroga");
  const mes = activos.filter((f) => f._r.estado === "avisa");
  const bien = activos.filter((f) => f._r.estado === "ok");
  const conP = activos.filter((f) => f._r.peligrosas);
  const pMal = conP.filter((f) => f._r.faltan.some((x) => x.includes("peligrosas")));

  /* Las zonas que la lista no reconoce. NO se esconden: una zona mal escrita
   * le saca la prórroga a ese chofer sin fallar en ningún lado, y el síntoma
   * —«no puede salir»— no señala a la lista de zonas. Se arregla con una línea
   * en `shared/zona.js`, pero primero hay que saber cuál. */
  const zonasRaras = useMemo(() => desconocidas(activos), [activos]);

  /* Y las siglas de empresa que el glosario no tiene. Van aparte de las zonas
   * porque se arreglan en archivos distintos y no cuestan lo mismo: una zona
   * desconocida le SACA la prórroga a alguien, y una sigla desconocida sólo se
   * muestra sin traducir. */
  const empresasRaras = useMemo(() => siglasRaras(activos), [activos]);

  /* Cuántos faltan por cargar. El número va a la vista porque es lo único que
   * hace que la columna se llene: «12 de larga distancia» sobre 224 se lee
   * como un inventario terminado. Misma regla que la lona. */
  const tipos = useMemo(() => contarTipos(activos), [activos]);

  const deLaSolapa =
    vista === "resolver" ? resolver
      : vista === "prorroga" ? conProrroga
        : vista === "mes" ? mes
          : vista === "baja" ? deBaja
            // «Todos» son todos los que MANEJAN. Los de baja tienen su solapa:
            // metidos acá, esconderlos no serviría de nada y la lista de
            // trabajo volvería a tener adentro a los que no hay que atender.
            : activos;

  /* La solapa, más las recién corregidas que ya no le tocan. Se ordenan con el
   * veredicto de ANTES para que no se muevan, y se dibujan con el de ahora. */
  const yaEstan = new Set(deLaSolapa.map((f) => f.dni));
  const corregidas = quietas.size
    ? conVeredicto.filter((f) => quietas.has(f.dni) && !yaEstan.has(f.dni))
    : [];
  const base = corregidas.length
    ? ordenar([
        ...deLaSolapa,
        ...corregidas.map((f) => ({ ...f, _r: quietas.get(f.dni), _ahora: f })),
      ]).map((f) => f._ahora || f)
    : deLaSolapa;

  /* La búsqueda le gana al corte, igual que en los padrones: buscar por nombre
   * es preguntar por alguien concreto, y contestar "no hay resultados" porque
   * la solapa lo tapaba es el peor no que puede dar un buscador. */
  /* Y BUSCA EN TODAS LAS COLUMNAS, no sólo en las dos de la izquierda. La
   * regla vive en `shared/documentacion.js` junto a la del orden, porque es la
   * misma pregunta —«qué muestra la celda»— y con las dos separadas se podría
   * ordenar por una columna que no se puede buscar. El PIN queda afuera a
   * propósito: está detrás de un ojo por algo. */
  const q = texto.trim();
  const filtrada = q ? conVeredicto.filter((f) => coincide(f, q)) : base;

  /* P-113: el corte por fecha se aplica DESPUÉS de la solapa y la búsqueda —
   * recorta lo que ya se está mirando. Los sin vencimiento cargado quedan
   * AFUERA a propósito: la pregunta es «¿a quién se le vence antes de X?», y
   * un sin-dato no se le vence antes de nada — ése vive en «resolver». */
  const cortada = hasta
    ? filtrada.filter((f) => f.registro_vence && f.registro_vence <= hasta)
    : filtrada;

  /* El orden por columna se aplica AL FINAL, sobre lo que ya se filtró: la
   * solapa y la búsqueda deciden QUIÉNES, y esto en qué orden. */
  const lista = useMemo(
    () => (orden.columna ? ordenarPor(cortada, orden.columna, orden.desc) : cortada),
    [cortada, orden],
  );

  /* Ascendente → descendente → el orden de urgencia de siempre. */
  const tocarColumna = (col) =>
    setOrden((o) =>
      o.columna !== col
        ? { columna: col, desc: false }
        : o.desc
          ? { columna: null, desc: false }
          : { columna: col, desc: true },
    );

  /* ── La carga en tanda ───────────────────────────────────────────────
   *
   * El primer campo que la usa es «hace cargas peligrosas», que es un sí/no
   * que hoy se marca de a uno. Cuando entre larga distancia / movimientos, se
   * suma como una opción más: la máquina es la misma. */
  /* ── LOS DOS CAMPOS QUE SE CARGAN DE A MUCHOS ────────────────────────
   *
   * `requiere_peligrosas` estuvo un rato afuera de esta lista con razón: el
   * volcado de la planilla lo pisaba **sin `coalesce`** —era el único campo
   * así, porque lo deriva del «(P)» del nombre—, o sea que lo que se cargara a
   * mano se borraba esa misma noche a las 03:50. Un campo que se guarda, dice
   * «listo» y amanece vacío es peor que uno que no se puede editar.
   *
   * Ya no: la planilla lo siembra al insertar una fila nueva y después no lo
   * toca. El hub es la fuente de verdad y se edita desde acá. */
  const CAMPOS_BULK = [
    {
      campo: "tipo_servicio",
      etiqueta: "Servicio",
      opciones: [
        { valor: LARGA, etiqueta: "Larga distancia" },
        { valor: MOV, etiqueta: "Movimientos" },
        { valor: null, etiqueta: "Sin dato (borrar)" },
      ],
    },
    {
      campo: "requiere_peligrosas",
      etiqueta: "Cargas peligrosas",
      opciones: [
        { valor: true, etiqueta: "Hace cargas peligrosas" },
        { valor: false, etiqueta: "No hace cargas peligrosas" },
      ],
    },
  ];

  const seleccionadas = useMemo(
    () => marcadas(seleccion, conVeredicto),
    [seleccion, conVeredicto],
  );
  const resSel = useMemo(
    () => resumenSeleccion(seleccion, lista),
    [seleccion, lista],
  );

  const aplicarBulk = async (campo, valor) => {
    setGuardandoBulk(true);
    setResultadoBulk(null);
    const dnis = seleccionadas.map((f) => f.dni);
    try {
      const r = await corregirVarios(dnis, { [campo]: valor });
      /* Se escribe en memoria lo que entró, sin volver a pedir la lista: con
       * la tabla filtrada y ordenada, recargar mueve todo de lugar justo
       * después de tocar cuarenta filas. */
      const entraron = new Set(r.ok);
      setFilas((prev) => (prev || []).map(
        (f) => (entraron.has(f.dni) ? { ...f, [campo]: valor } : f)));
      /* LAS QUE FALLARON SIGUEN MARCADAS, y las que entraron se sueltan. Así
       * el segundo intento es apretar de nuevo y no volver a buscarlas una por
       * una — y la barra sigue diciendo cuántas quedan. */
      setSeleccion(new Set(r.fallaron.map((x) => String(x.dni))));
      setResultadoBulk({ ok: r.ok.length, fallaron: r.fallaron.length });
    } catch (e) {
      setResultadoBulk({ ok: 0, fallaron: dnis.length });
    } finally {
      setGuardandoBulk(false);
    }
  };

  const guardar = async (dni, campo, valor) => {
    const antes = conVeredicto.find((f) => f.dni === dni)?._r;
    await corregirDocumento(dni, { [campo]: valor });
    // Se anota ANTES de escribir la fila: si al cambiar se va de la solapa,
    // sigue a la vista con su ✓ (ver `quietas`).
    setQuietas((m) => (!antes || m.has(dni) ? m : new Map(m).set(dni, antes)));
    setFilas((prev) =>
      (prev || []).map((f) => (f.dni === dni ? { ...f, [campo]: valor } : f)),
    );
  };

  /* La baja se escribe en memoria y no se vuelve a pedir la lista: recargar
   * mueve todo de lugar justo después de tocar una fila. Y el que se acaba de
   * dar de baja SE SIGUE VIENDO hasta que se cambie de solapa —cae en «de
   * baja», que puede estar filtrada— así que la fila desaparecería del medio de
   * la tabla sin decir a dónde fue. Por eso el aviso de arriba cuenta cuántos
   * hay y ofrece la solapa. */
  const cambiarBaja = async (dni, activo) => {
    await cambiarActivo(dni, activo);
    setFilas((prev) =>
      (prev || []).map((f) => (f.dni === dni ? { ...f, activo } : f)),
    );
  };

  if (error && !filas) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }
  if (!filas) return <Loading />;

  return (
    <div className="docs">
      <div className="conteos">
        <div className="conteo mal">
          <b>{resolver.length}</b>
          <span>
            no pueden salir hoy
            <br />
            registro vencido o sin fecha
          </span>
        </div>
        {/* LA PRÓRROGA TIENE SU PROPIO NÚMERO Y NO SE SUMA A NINGUNO DE LOS
            OTROS DOS. Metida en «no pueden salir» frena a gente que sí puede;
            metida en «en condiciones» esconde un reloj corriendo y el registro
            se renueva el día 61. Sólo aparece si hay alguien: un «0 con
            prórroga» es un cartel que hay que leer para descartar. */}
        {conProrroga.length > 0 && (
          <div className="conteo prorroga">
            <b>{conProrroga.length}</b>
            <span>
              con prórroga vigente
              <br />
              pueden salir · Buenos Aires
            </span>
          </div>
        )}
        <div className="conteo ojo">
          <b>{mes.length}</b>
          <span>vencen en los próximos {DIAS_AVISO} días</span>
        </div>
        <div className="conteo ok">
          <b>{bien.length}</b>
          <span>en condiciones</span>
        </div>
        <div className="conteo">
          <b>{pMal.length}</b>
          <span>
            peligrosas vencidas
            <br />
            de los {conP.length} choferes (P)
          </span>
        </div>
      </div>

      <div className="tk-filtros">
        {VISTAS
          /* Sin nadie de baja la solapa no se dibuja: un «De baja · 0» es un
             cartel que hay que leer para descartar. Misma regla que el conteo
             de la prórroga. */
          .filter(([v]) => v !== "baja" || deBaja.length > 0)
          .map(([v, n]) => (
            <button
              key={v}
              className={"tk-chip" + (vista === v && !q ? " activo" : "")}
              onClick={() => {
                setVista(v);
                setTexto("");
                soltarQuietas();
              }}
            >
              {n} ·{" "}
              {v === "resolver"
                ? resolver.length
                : v === "prorroga"
                  ? conProrroga.length
                  : v === "mes"
                    ? mes.length
                    : v === "baja"
                      ? deBaja.length
                      : activos.length}
            </button>
          ))}
        <ModoEdicion
          activo={editando}
          onCambiar={(v) => {
            setEditando(v);
            soltarQuietas();
          }}
          puede={puedeEditar}
          que="los vencimientos"
        />
        {/* Traer la planilla se hace ACÁ, que es donde se mira el resultado.
            El botón existía desde hacía rato pero en `/documentos` del panel de
            FlotaBot — otra pantalla, a la que desde el hub no se llega. Un
            botón que no está donde se hace el trabajo es lo mismo que no
            estar, y el síntoma era esta tabla mostrando DNIs en vez de
            nombres. */}
        {/* El alta vive con el lápiz: agregar un chofer es editar. Y va acá
            y no en otra pantalla — es la lección del botón de la planilla, que
            existía en `/documentos` de FlotaBot y por eso no lo apretaba
            nadie. */}
        {tocando && !dandoDeAlta && (
          <button
            type="button"
            className="modo-edicion"
            onClick={() => setDandoDeAlta(true)}
          >
            <span aria-hidden="true">＋</span> Agregar chofer
          </button>
        )}
        {/* La foto del registro vive con el lápiz, igual que el alta: soltarla
            es cargar un vencimiento. Y va en ESTA pantalla y no en otra — es
            la lección del botón de la planilla. */}
        {tocando && !soltando && (
          <button
            type="button"
            className="modo-edicion"
            onClick={() => setSoltando(true)}
          >
            <span aria-hidden="true">🪪</span> Leer un registro
          </button>
        )}
        {/* SÓLO PARA EL ADMIN (P-110, Aldo): para quien trabaja la planilla
            estos dos botones eran ruido con dientes — «Sincronizar» y el PIN
            son operaciones de sistema, no del trabajo diario. No desaparecen
            del sistema: quien administra los sigue teniendo acá. Y acá se
            esconde hasta saber (al revés de la regla del lápiz): el usuario
            común NO es admin, y mostrarle los botones medio segundo es
            mostrárselos. */}
        {esAdmin && <TraerPlanilla alTerminar={traer} />}
      </div>

      {/* EL BUSCADOR VA AFUERA DE LA FILA DE SOLAPAS, y no es cosmético.
        * `.permisos-buscar` mide `width: 100%`, así que adentro de un flex que
        * envuelve se come el renglón entero y empuja abajo a todo lo que venga
        * después — el lápiz y los botones de traer la planilla quedaban
        * colgados DEBAJO del buscador, que es el único lugar donde nadie los
        * busca. El botón estaba en la pantalla y aun así "no estaba". */}
      <input
        className="permisos-buscar"
        type="search"
        placeholder="Buscar: nombre, DNI, empresa, zona, teléfono, fecha…"
        value={texto}
        onChange={(e) => {
          setTexto(e.target.value);
          soltarQuietas();
        }}
        aria-label="Buscar chofer"
      />

      {/* P-113 (Aldo): el filtro por fecha, en la cabecera de la planilla.
          Recorta lo que ya se está mirando a los registros que vencen HASTA
          esa fecha — la pregunta de renovaciones («¿a quién se le vence antes
          de fin de mes?») en un solo control. */}
      <label className="docs-hasta">
        Registro vence hasta:{" "}
        <input
          type="date"
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
          aria-label="Mostrar sólo registros que vencen hasta esta fecha"
        />
        {hasta && (
          <button type="button" className="tk-chip"
                  onClick={() => setHasta("")}>
            quitar
          </button>
        )}
      </label>

      {q && (
        <p className="table-escapan">
          Buscando en los {conVeredicto.length} choferes, sin importar la solapa
          {deBaja.length > 0 && " y con los dados de baja adentro"}.
        </p>
      )}

      {/* CUÁNTOS QUEDARON AFUERA DE LOS NÚMEROS DE ARRIBA. Un chofer de baja no
          se esconde en silencio: los conteos cambiarían solos y nadie sabría
          por qué. Es la misma regla que ya vale para los que se escapan del
          filtro en los padrones. */}
      {deBaja.length > 0 && vista !== "baja" && !q && (
        <p className="table-escapan">
          <b>{deBaja.length}</b>{" "}
          {deBaja.length === 1 ? "chofer está" : "choferes están"} de baja y no
          {deBaja.length === 1 ? " cuenta" : " cuentan"} en estos números.{" "}
          <button type="button" className="link-button" onClick={() => setVista("baja")}>
            Ver {deBaja.length === 1 ? "cuál" : "cuáles"}
          </button>
        </p>
      )}

      {/* Las zonas que no reconocemos, JUNTAS y con cuántos hay de cada una.
          No se esconden y no se adivinan: sin la zona no se puede saber si a
          esa persona le corresponde la prórroga, y el error cae del lado de
          frenarla. Se arregla con una línea en `shared/zona.js` — pero primero
          hay que saber cuál falta. Misma idea que los permisos desconocidos de
          la planilla. */}
      {zonasRaras.length > 0 && (
        <p className="panel-aviso suave">
          <b>No reconozco {zonasRaras.length === 1 ? "esta zona" : "estas zonas"}</b>
          , así que esos choferes no reciben la prórroga de {DIAS_PRORROGA} días
          aunque les corresponda:{" "}
          {zonasRaras.map((z) => `${z.zona} (${z.choferes})`).join(" · ")}.
        </p>
      )}

      {/* CUÁNTOS FALTAN, que es el número que hace que la columna se llene.
          «12 de larga distancia» sobre 224 se lee como un inventario
          terminado; con los que faltan al lado se lee como lo que es. Se
          esconde cuando no falta ninguno: un «0 sin cargar» es un cartel que
          hay que leer para descartar. Misma regla que la lona. */}
      {tipos.faltan > 0 && (
        <p className="table-escapan">
          <b>{tipos.faltan}</b> de {tipos.total} choferes todavía no tienen
          cargado si son de larga distancia o de movimientos
          {tipos.faltan === tipos.total
            ? "."
            : ` (${tipos.larga} larga · ${tipos.mov} movimientos).`}
          {" "}Se cargan de a varios con el lápiz.
        </p>
      )}

      {empresasRaras.length > 0 && (
        <p className="panel-aviso suave">
          <b>Esta sigla de empresa no está en el glosario</b>:{" "}
          {empresasRaras.map((e) => `${e.sigla} (${e.choferes})`).join(" · ")}.
        </p>
      )}

      {dandoDeAlta && (
        <AltaChofer
          guardar={crearChofer}
          onCancelar={() => setDandoDeAlta(false)}
          onCreado={(fila) => {
            /* Se agrega en memoria en vez de volver a pedir la lista: con la
             * tabla filtrada y ordenada, recargar mueve todo de lugar justo
             * después de cargar a alguien. */
            setFilas((prev) => [...(prev || []), fila]);
            setDandoDeAlta(false);
            // Se lo muestra: un alta que desaparece bajo el filtro «hay que
            // resolver» se lee como que no se guardó.
            setTexto(String(fila.dni));
          }}
        />
      )}

      {soltando && (
        <SoltarRegistro
          filas={filas || []}
          onCerrar={() => setSoltando(false)}
          onGuardar={async (dniChofer, cambios) => {
            /* Pasa por el mismo `corregirDocumento` que una corrección a mano,
             * así que la fila queda sellada con QUIÉN apretó Ok. Ése es el
             * punto entero del «Ok / Editar»: lo que leyó un modelo se guarda
             * a nombre de la persona que lo miró, no del servidor. */
            await corregirDocumento(dniChofer, cambios);
            setFilas((prev) =>
              (prev || []).map((f) =>
                String(f.dni) === String(dniChofer) ? { ...f, ...cambios } : f,
              ),
            );
            // Se lo muestra, como después de un alta: una fila que cambió en
            // medio de una tabla filtrada no se ve, y parece que no se guardó.
            setTexto(String(dniChofer));
          }}
        />
      )}

      <div className="tabla-wrap">
        <table className={"tabla fija" + (tocando ? " editando" : "")}>
          <thead>
            <tr>
              {/* La casilla sólo existe en modo edición: fuera de él la tabla
                  se mira, y una columna de tildes invita a tocar. */}
              {tocando && (
                <th className="col-tilde">
                  <input
                    type="checkbox"
                    checked={todasVisiblesMarcadas(seleccion, lista)}
                    onChange={(e) =>
                      setSeleccion(
                        e.target.checked
                          ? marcarVisibles(seleccion, lista)
                          : desmarcarVisibles(seleccion, lista),
                      )
                    }
                    aria-label="Marcar los que se están viendo"
                    title="Marca lo que está en pantalla, no el padrón entero"
                  />
                </th>
              )}
              <ThOrden
                columna="chofer"
                orden={orden}
                alTocar={tocarColumna}
                titulo="Ordenar por nombre (A-Z, Z-A, o volver al orden por urgencia)"
              >
                Chofer
              </ThOrden>
              <ThOrden
                columna="registro"
                orden={orden}
                alTocar={tocarColumna}
                titulo="Ordenar por vencimiento del registro (del más viejo al más nuevo, al revés, o volver al orden por urgencia)"
              >
                Registro
              </ThOrden>
              <ThOrden columna="empresa" orden={orden} alTocar={tocarColumna}
                       titulo="Ordenar por empresa (o volver al orden por urgencia)">
                Empresa
              </ThOrden>
              <ThOrden columna="servicio" orden={orden} alTocar={tocarColumna}
                       titulo="Ordenar por tipo de servicio (o volver al orden por urgencia)">
                Servicio
              </ThOrden>
              <ThOrden columna="peligrosas" orden={orden} alTocar={tocarColumna}
                       titulo="Ordenar por vencimiento de cargas peligrosas. Los que no las necesitan van últimos">
                Cargas peligrosas
              </ThOrden>
              <ThOrden columna="zona" orden={orden} alTocar={tocarColumna}
                       titulo="Ordenar por zona (o volver al orden por urgencia)">
                Zona
              </ThOrden>
              {/* Al lado de Zona pero en su propia columna: la zona es el
                  PARTIDO y decide la prórroga de 60 días, la dirección es la
                  calle. Fundidas en una celda, la próxima persona escribe una
                  en la otra y ese chofer pierde la prórroga sin que nada
                  falle. */}
              <ThOrden columna="direccion" orden={orden} alTocar={tocarColumna}
                       titulo="Ordenar por dirección. Los que no tienen van últimos">
                Dirección
              </ThOrden>
              <ThOrden columna="telefono" orden={orden} alTocar={tocarColumna}
                       titulo="Ordenar por teléfono. Los que no tienen van últimos">
                Teléfono
              </ThOrden>
              {/* Observaciones (#105, Marcos): la nota libre de la persona —
                  «avisar después de las 14», «trae el carnet el lunes». Es la
                  columna `anotaciones`, NO la observación de la planilla: por
                  aquélla viajan la zona y la prórroga, y mezclarlas haría que
                  una nota de horario pise un dato que decide si puede salir. */}
              <ThOrden columna="anotaciones" orden={orden} alTocar={tocarColumna}
                       titulo="Ordenar por observaciones. Los que no tienen van últimos">
                Observaciones
              </ThOrden>
              {/* EL PIN NO SE ORDENA, y es la única que no. Es una credencial
                  de pago: ordenar por su valor la pondría en fila y la haría
                  legible de un vistazo a quien tenga `ver.pin_ypf` — y no hay
                  ninguna pregunta que eso conteste. */}
              <th>PIN</th>
              {/* QUIÉN TOCÓ LA FILA. Estos vencimientos ahora se corrigen desde
                  acá, así que un dato mal cargado tiene que poder terminar en
                  una corrección y no en un «yo no fui». El hover dice el
                  nombre, el mail y cuándo. */}
              <ThOrden columna="cargo" orden={orden} alTocar={tocarColumna}
                       titulo="Ordenar por quién cargó la fila. Lo que trajo la planilla no tiene autor y va último">
                Cargó
              </ThOrden>
            </tr>
          </thead>
          <tbody>
            {lista.map((f) => (
              <tr
                key={f.dni}
                /* EL SEMÁFORO NO SE PINTA SOBRE UN CHOFER DE BAJA. Su registro
                   puede estar vencido y es verdad, pero la fila en rojo se lee
                   como trabajo pendiente y es justo lo que no hay que atender.
                   Queda en gris, que es lo que dice «esto es archivo». */
                className={
                  estaDeBaja(f)
                    ? "tenue"
                    : f._r.estado === "faltan"
                      ? "mal"
                      : f._r.estado === "prorroga"
                        ? "prorroga"
                        : f._r.estado === "avisa"
                          ? "ojo"
                          : ""
                }
              >
                {/* SIN NOMBRE SE DICE, no se deja el renglón en blanco. Con
                    `<b>{f.nombre}</b>` a secas, una fila sin nombre quedaba
                    como un número gris suelto y se leía como una pantalla a
                    medio cargar. Son 8 de 210 y no es un error: están en la
                    planilla de documentación y no en el padrón, así que su
                    nombre sólo puede venir de la planilla. El DNI pasa al
                    renglón de arriba porque ahí es lo único que identifica a
                    esa persona, y va etiquetado — un número pelado en negrita
                    no dice qué número es. */}
                {tocando && (
                  <td className="col-tilde">
                    <input
                      type="checkbox"
                      checked={seleccion.has(String(f.dni))}
                      onChange={() =>
                        setSeleccion((s) => alternar(s, f.dni))
                      }
                      aria-label={`Marcar a ${f.nombre || f.dni}`}
                    />
                  </td>
                )}
                <td>
                  {f.nombre ? (
                    <>
                      <b>{f.nombre}</b>
                      <span className="sub">DNI {f.dni}</span>
                    </>
                  ) : (
                    <>
                      <b>DNI {f.dni}</b>
                      <span className="sub sin-dato">sin nombre cargado</span>
                    </>
                  )}
                  {/* LA MARCA SE VE SIEMPRE, no sólo con el lápiz prendido.
                      Es lo que hace que buscar a alguien dado de baja conteste
                      «está de baja» y no «no existe» — que es otra afirmación y
                      es falsa. Misma regla que las unidades de baja en el
                      buscador. */}
                  {estaDeBaja(f) && (
                    <span className="sub sin-dato">de baja · no cuenta en los avisos</span>
                  )}
                  {/* P-103 (Marcos): el nombre también se corrige en la celda.
                      Es EL caso que lo pidió — la máquina leyó «Alborn0z» y no
                      había desde dónde arreglarlo. */}
                  {tocando && (
                    <CampoEditable
                      valor={f.nombre}
                      onGuardar={(v) => guardar(f.dni, "nombre", v)}
                      etiqueta="Nombre"
                      placeholder="Apellido, Nombre"
                    />
                  )}
                  {tocando && (
                    <BotonBaja
                      deBaja={estaDeBaja(f)}
                      onCambiar={(activo) => cambiarBaja(f.dni, activo)}
                    />
                  )}
                </td>
                <td>
                  <Celda fecha={f.registro_vence} prorroga={f._r.prorroga} />
                  <FechaEditable
                    valor={f.registro_vence}
                    puedeEditar={tocando}
                    soloEdicion
                    onGuardar={(v) => guardar(f.dni, "registro_vence", v)}
                  />
                  {/* El psicofísico, que era una columna y ahora es un
                      requisito de ésta: hace falta para RENOVAR el registro. */}
                  <PsicofisicoDelRegistro
                    fila={f}
                    tocando={tocando}
                    onGuardar={(v) => guardar(f.dni, "psicofisico_vence", v)}
                  />
                </td>
                {/* La empresa deja de ir chiquita al lado del DNI y pasa a
                    su columna: el dato ya estaba, y perdido en el subtítulo no
                    se podía recorrer de un vistazo ni ordenar por él. La sigla
                    va abajo porque es lo que está escrito en la planilla — sin
                    ella, quien vaya a corregirla no sabe qué buscar. */}
                {/* LA SIGLA MANDA (#106, Marcos): con 224 filas el nombre
                    completo no se recorre de un vistazo. El nombre entero
                    queda en el subtítulo — es lo que necesita quien no conoce
                    la sigla, y el hover del título lo repite. */}
                <td title={f.empresa ? empresaDe(f.empresa) : undefined}>
                  {f.empresa ? (
                    <>
                      <b>{String(f.empresa).trim()}</b>
                      <span className="sub">{empresaDe(f.empresa)}</span>
                    </>
                  ) : (
                    <span className="sub sin-dato">sin empresa</span>
                  )}
                  {tocando && (
                    <CampoEditable
                      valor={f.empresa}
                      onGuardar={(v) => guardar(f.dni, "empresa", v)}
                      etiqueta="Empresa"
                      placeholder="Sigla (TRC, TCC…)"
                    />
                  )}
                </td>
                {/* Sin dato NO se dibuja como «movimientos»: es el estado en
                    el que están los 224 el primer día, y confundirlos haría
                    que la lista de larga distancia arranque vacía y nadie vea
                    el trabajo pendiente. */}
                {/* Abreviado (#107, Marcos): «Larga» y «Mov.» alcanzan para
                    leer la columna, y el título dice el nombre completo. La
                    etiqueta larga sigue viva en los selects de edición. */}
                <td title={etiquetaTipo(f.tipo_servicio)}>
                  {tipoDe(f.tipo_servicio) === TIPO_SIN_DATO ? (
                    <span className="sub sin-dato">sin dato</span>
                  ) : tipoDe(f.tipo_servicio) === LARGA ? (
                    "Larga"
                  ) : tipoDe(f.tipo_servicio) === MOV ? (
                    "Mov."
                  ) : (
                    etiquetaTipo(f.tipo_servicio)
                  )}
                  {/* Un select y no un campo libre: el servicio es un
                      vocabulario de dos —tipearlo inventaría un tercero. Son
                      las mismas opciones de la carga en tanda. */}
                  {tocando && (
                    <select
                      className="doc-select"
                      value={tipoDe(f.tipo_servicio) === LARGA ? LARGA
                        : tipoDe(f.tipo_servicio) === MOV ? MOV : ""}
                      onChange={(e) =>
                        guardar(f.dni, "tipo_servicio", e.target.value || null)}
                      aria-label={`Servicio de ${f.nombre || f.dni}`}
                    >
                      <option value="">sin dato</option>
                      <option value={LARGA}>Larga distancia</option>
                      <option value={MOV}>Movimientos</option>
                    </select>
                  )}
                </td>
                <td>
                  <Celda
                    fecha={f.peligrosas_vence}
                    haceFalta={f._r.peligrosas}
                  />
                  {f._r.peligrosas && (
                    <FechaEditable
                      valor={f.peligrosas_vence}
                      puedeEditar={tocando}
                      soloEdicion
                      onGuardar={(v) => guardar(f.dni, "peligrosas_vence", v)}
                    />
                  )}
                </td>
                {/* La zona es el PARTIDO y decide la prórroga de 60 días: por
                    eso también tiene que poder corregirse — un «Tigre» mal
                    tipeado le saca la prórroga a alguien sin fallar en ningún
                    lado. */}
                <td className="tenue">
                  {tocando ? (
                    <CampoEditable
                      valor={f.zona}
                      onGuardar={(v) => guardar(f.dni, "zona", v)}
                      etiqueta="Zona"
                      placeholder="Partido (Tigre, Pilar…)"
                    />
                  ) : (
                    f.zona || "—"
                  )}
                </td>
                <td>
                  <DireccionEditable
                    valor={f.direccion}
                    puedeEditar={tocando}
                    onGuardar={(v) => guardar(f.dni, "direccion", v)}
                  />
                </td>
                <td>
                  <Telefono tel={f.telefono} nombre={f.nombre} />
                  <TelefonoEditable
                    valor={f.telefono}
                    puedeEditar={tocando}
                    onGuardar={(v) => guardar(f.dni, "telefono", v)}
                  />
                </td>
                {/* La nota libre, editable con el mismo lápiz que el resto.
                    Escribe `anotaciones` vía `guardar`, así queda sellada con
                    quién la escribió — una nota anónima del estilo «no lo
                    llames» es justo la que después nadie puede defender. */}
                <td>
                  {tocando ? (
                    <CampoEditable
                      valor={f.anotaciones}
                      onGuardar={(v) => guardar(f.dni, "anotaciones", v)}
                      etiqueta="Observaciones"
                      placeholder="Nota libre"
                    />
                  ) : (
                    <span className="tenue">{f.anotaciones || "—"}</span>
                  )}
                </td>
                <td>
                  <PinYPF
                    pin={pins?.[String(f.dni)]}
                    puedeVer={puedeVerPin}
                    hayTabla={Boolean(pins && Object.keys(pins).length)}
                  />
                </td>
                <td>
                  {/* Lo que trae la planilla no tiene autor, y ahí va un guion:
                      es una carga del sistema, no de una persona. Sólo lo
                      corregido desde acá lleva nombre. */}
                  <ChipUsuario fila={f} size={22} />
                </td>
              </tr>
            ))}
            {lista.length === 0 && (
              <tr>
                <td colSpan={tocando ? 11 : 10} className="vacio">
                  {q
                    ? "Ningún chofer con ese nombre o DNI."
                    : conVeredicto.length === 0
                      ? "Todavía no hay documentación cargada."
                      : "No hay nadie en este grupo."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Va fija abajo y fuera de la tabla: con 224 filas, una barra que se
          desplaza obliga a subir hasta arriba para aplicar lo que se acaba de
          marcar abajo. */}
      {tocando && (
        <BarraSeleccion
          resumen={resSel}
          filasMarcadas={seleccionadas}
          campos={CAMPOS_BULK}
          onAplicar={aplicarBulk}
          onLimpiar={() => {
            setSeleccion(new Set());
            setResultadoBulk(null);
          }}
          guardando={guardandoBulk}
          resultado={resultadoBulk}
        />
      )}

    </div>
  );
};

export default Documentacion;
